'use client'

import { useCallback, useEffect, useRef } from 'react'
import { NewType, SliderSettings } from '@/types'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Swiper as SwiperType } from 'swiper'
import 'swiper/css'
import CatalogCategory from '@/components/catalog/CatalogCategory'
import NewsCard from '../news/NewsCard'
import CatalogProdCard from '../catalog/CatalogProdCard'
import MaterialsCategory from '../useful-materials/MaterialsCategory'
import { useRouter } from 'next/navigation'
import TextRing from '../TextRing'
import { useTranslation } from 'react-i18next'

type Props = {
    settings: SliderSettings[]
    type: string
    list: any[] | NewType[]
    linkedCards?: boolean
    zIndex?: number
    materialsNavNext?: boolean
}

const getBreakPoints = settingsArr => {
    return {
        320: {
            slidesPerView: 1.5,
            initialSlide: settingsArr.mobile.initialSlide,
            spaceBetween: 10
        },
        340: {
            slidesPerView: 1.6,
            spaceBetween: 10
        },
        380: {
            slidesPerView: 1.8,
            spaceBetween: 10
        },
        400: {
            slidesPerView: 2,
            spaceBetween: 10
        },
        480: {
            slidesPerView: 2.2,
            spaceBetween: 10
        },
        500: {
            slidesPerView: 2.3,
            spaceBetween: 10
        },
        560: {
            slidesPerView: 2.7,
            spaceBetween: 10
        },
        620: {
            slidesPerView: 2.8,
            spaceBetween: 10
        },
        676: {
            slidesPerView: 3.1,
            spaceBetween: 20
        },
        730: {
            slidesPerView: 3.2,
            spaceBetween: 20
        },
        770: {
            slidesPerView: 3.3,
            spaceBetween: 20
        },
        810: {
            slidesPerView: 3.5,
            spaceBetween: 20
        },
        870: {
            slidesPerView: 3.7,
            spaceBetween: 20
        },
        940: {
            slidesPerView: 4,
            spaceBetween: 20
        },
        1000: {
            slidesPerView: 4.2,
            spaceBetween: 20
        },
        1060: {
            slidesPerView: 4.7,
            spaceBetween: 20
        },
        1100: {
            slidesPerView: 4.9,
            spaceBetween: 20
        },
        1150: {
            slidesPerView: 5.1,
            spaceBetween: 20
        },
        1250: {
            slidesPerView: 4.5,
            spaceBetween: 20
        },
        1320: {
            slidesPerView: 4.8,
            spaceBetween: 20
        },
        1360: {
            slidesPerView: 5,
            spaceBetween: 20
        },
        1400: {
            slidesPerView: 5.1,
            spaceBetween: 20
        },
        1450: {
            slidesPerView: 5.3,
            spaceBetween: 20
        },
        1550: {
            slidesPerView: 5.5,
            spaceBetween: 20
        },
        1650: {
            slidesPerView: 6,
            spaceBetween: 20
        },
        1750: {
            slidesPerView: 6.3,
            spaceBetween: 20
        },
        1800: {
            slidesPerView: 6.5,
            spaceBetween: 20
        },
        1850: {
            slidesPerView: 6.7,
            spaceBetween: 20
        },
        1900: {
            slidesPerView: 6.9,
            spaceBetween: 20
        },
        1940: {
            slidesPerView: 7,
            spaceBetween: 20
        },
        1960: {
            slidesPerView: 7.2,
            spaceBetween: 20
        },
        2100: {
            slidesPerView: 7.5,
            spaceBetween: 20
        },
        2200: {
            slidesPerView: 7.7,
            spaceBetween: 20
        },
        2250: {
            slidesPerView: 7.9,
            spaceBetween: 20
        },
        2300: {
            slidesPerView: 8.2,
            spaceBetween: 20
        },
        2350: {
            slidesPerView: 8.5,
            spaceBetween: 20
        }
    }
}

const MainSlider: React.FC<Props> = ({ settings, type, list, linkedCards, zIndex, materialsNavNext }) => {
    const swiperRef = useRef<SwiperType>()
    const settingsArr = settings[0]
    const router = useRouter()
    const { t } = useTranslation()

    const defineVisivleSlides = useCallback(() => {
        if (type === 'useful-materials') {
            return 6
        }
        if (type === 'catalog') {
            return 6
        }
        if (type === 'materials-products') {
            return 6
        }
    }, [type])

    useEffect(() => {
        if (list?.length < 10) {
            const additionalSlides = 10 - list.length
            const copiedSlides = Array.from({ length: additionalSlides }, (_, index) => list[index % list.length])
            list = list.concat(copiedSlides)
        }
    }, [])

    return (
        settings && (
            <Swiper
                style={zIndex ? { position: 'relative', zIndex: zIndex } : {}}
                spaceBetween={10}
                className={`${settingsArr.className}`}
                slidesPerView={defineVisivleSlides()}
                loop={true}
                onBeforeInit={swiper => {
                    swiperRef.current = swiper
                }}
                centeredSlides={true}
                centeredSlidesBounds={true}
                breakpoints={getBreakPoints(settingsArr)}
            >
                {list &&
                    list?.map((slide, i) => (
                        <SwiperSlide key={i}>
                            {type === 'news' ? (
                                <NewsCard card={slide} />
                            ) : type === 'materials-products' ? (
                                <CatalogProdCard
                                    type={type}
                                    item={slide}
                                />
                            ) : type === 'useful-materials' ? (
                                <MaterialsCategory
                                    type='main-slider'
                                    linked={true}
                                    item={slide}
                                />
                            ) : (
                                <CatalogCategory
                                    linked={linkedCards}
                                    classItem={'categories-slider__card-reverse'}
                                    item={slide}
                                />
                            )}
                        </SwiperSlide>
                    ))}
                {type !== 'news' && type !== 'materials-products' && materialsNavNext && (
                    <div className='main__materials-nav-block container'>
                        <div
                            onClick={() =>
                                type === 'catalog' ? router.push('/catalog') : router.push('/useful-materials')
                            }
                            className='main__materials-nav main__materials-nav_next'
                        >
                            <svg
                                width='20'
                                height='20'
                                viewBox='0 0 20 20'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                            >
                                <path
                                    d='M19.7071 10.7071C20.0976 10.3166 20.0976 9.68342 19.7071 9.29289L13.3431 2.92893C12.9526 2.53841 12.3195 2.53841 11.9289 2.92893C11.5384 3.31945 11.5384 3.95262 11.9289 4.34314L17.5858 10L11.9289 15.6569C11.5384 16.0474 11.5384 16.6805 11.9289 17.0711C12.3195 17.4616 12.9526 17.4616 13.3431 17.0711L19.7071 10.7071ZM1 9C0.447716 9 1.44847e-07 9.44771 0 10C-1.44847e-07 10.5523 0.447715 11 1 11L1 9ZM19 9L1 9L1 11L19 11L19 9Z'
                                    fill='url(#paint0_linear_4310_12982)'
                                />
                                <defs>
                                    <linearGradient
                                        id='paint0_linear_4310_12982'
                                        x1='4.62983'
                                        y1='9.62857'
                                        x2='16.1094'
                                        y2='3.54524'
                                        gradientUnits='userSpaceOnUse'
                                    >
                                        <stop stop-color='#FEAA9F' />
                                        <stop
                                            offset='1'
                                            stop-color='#FEC2BA'
                                        />
                                    </linearGradient>
                                </defs>
                            </svg>
                            {type === 'catalog' ? (
                                <TextRing side={1}>{t('catalogSwiperCircle')}</TextRing>
                            ) : (
                                <TextRing side={1.4}>{t('materialsSwiperCircle')}</TextRing>
                            )}
                        </div>
                    </div>
                )}
            </Swiper>
        )
    )
}

export default MainSlider
